/**
 * 크리에이터 지표(Apify 수집값) 저장소 — 저장 위치와 병합 규칙을 한곳에 모은 곳.
 *
 * 지금은 브라우저 localStorage 에 담는다. 백엔드에 지표 저장 필드가 생기면
 * loadMetricsCache / saveMetricsCache 두 함수만 API 호출로 바꾸면 되고,
 * 나머지(병합 가드·신선도 판정·출처 표기)는 그대로 쓴다.
 * → 요청 내용은 api-spec/BACKEND-TODO.md 의 "크리에이터 지표 저장 필드" 항목.
 *
 * ⚠️ localStorage 인 동안의 한계(대표님께 보고된 내용):
 *    담당자마다 캐시가 따로 놀아서, 같은 크리에이터를 사람 수만큼 중복 수집(=중복 과금)한다.
 */

/** 이 시간이 지난 값은 "오래됨"으로 표시한다(자동 재수집은 하지 않는다 — 돈이 나가므로) */
export const METRICS_TTL_HOURS = 24;

const CACHE_STORAGE_KEY = 'blbent_creator_metrics_cache_v1';
const CACHE_MAX_ENTRIES = 500; // 무한정 커지지 않게 오래된 것부터 정리

/** 저장 키 — 표시명(KOL ID)은 사람이 바꾸므로 절대 키로 쓰지 않는다. 플랫폼+핸들이 진짜 키. */
export function metricsKey(platform, handle) {
  return `${platform}:${String(handle || '').toLowerCase().trim()}`;
}

export function loadMetricsCache() {
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveMetricsCache(cache) {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(cache));
  } catch (err) {
    console.error('지표 캐시 저장 실패(무시):', err);
  }
}

/**
 * 수집 결과(item) → 저장할 형태.
 * 표시에 필요한 값만 남겨 용량을 아끼고, "언제 · 어디서 온 값인지"를 같이 찍는다.
 * 출처/시각이 없으면 화면에서 이 숫자를 믿어도 되는지 판단할 방법이 없다.
 */
export function toEntry(item, source = 'apify') {
  const m = item.metrics || {};
  return {
    handle: item.handle,
    platform: item.platform,
    ok: item.ok,
    profileUrl: item.profileUrl,
    source,
    collectedAt: new Date().toISOString(),
    cachedAt: Date.now(), // 이전 버전 캐시와의 호환용
    metrics: {
      followers: m.followers ?? null,
      engagementRate: m.engagementRate ?? null,
      medianViews: m.medianViews ?? null,
      metricsBasis: m.metricsBasis ?? null,
    },
  };
}

/**
 * 기존 값 + 새 값 → 저장할 값. **덮어쓰기 사고를 막는 유일한 지점.**
 *
 * 1) 수집 실패는 성공해서 들어와 있던 값을 절대 덮지 않는다
 *    (계정이 비공개로 바뀐 날 표의 숫자가 통째로 사라지는 사고 — 레이더에서 실제로 겪음)
 * 2) 성공했더라도 이번에 못 가져온 개별 지표(null)는 예전 값을 남긴다
 *    (팔로워만 긁는 "팔로워 새로고침"이 참여율을 지워버리는 것을 막음)
 */
export function mergeEntry(prev, next) {
  if (!next) return prev || null;
  if (!next.ok) return prev?.ok ? prev : next;
  if (!prev?.ok) return next;

  const metrics = { ...(prev.metrics || {}) };
  Object.entries(next.metrics || {}).forEach(([k, v]) => {
    if (v != null) metrics[k] = v;
  });
  return { ...next, metrics };
}

/** 항목 수 상한을 넘으면 오래된 것부터 버린다 */
export function pruneCache(cache) {
  const keys = Object.keys(cache);
  if (keys.length <= CACHE_MAX_ENTRIES) return cache;
  const next = { ...cache };
  keys
    .sort((a, b) => (next[a].cachedAt || 0) - (next[b].cachedAt || 0))
    .slice(0, keys.length - CACHE_MAX_ENTRIES)
    .forEach((k) => delete next[k]);
  return next;
}

/** 저장 시각(ISO 문자열 또는 ms) → ms */
function toMs(ts) {
  if (!ts) return null;
  const v = typeof ts === 'number' ? ts : Date.parse(ts);
  return Number.isFinite(v) ? v : null;
}

/** 수집한 지 오래됐는지 (자동 재수집은 안 함 — 화면에 "오래됨"만 띄우고 판단은 사람이) */
export function isStale(entry, hours = METRICS_TTL_HOURS) {
  const ms = toMs(entry?.collectedAt || entry?.cachedAt);
  if (ms == null) return true;
  return Date.now() - ms > hours * 3600 * 1000;
}

/** "3시간 전" 같은 사람 말 */
export function agoText(ts) {
  const ms = toMs(ts);
  if (ms == null) return '';
  const sec = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (sec < 60) return '방금 전';
  if (sec < 3600) return `${Math.floor(sec / 60)}분 전`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}시간 전`;
  return `${Math.floor(sec / 86400)}일 전`;
}

/** 출처 표기 — 이 숫자를 사람이 넣었는지 기계가 넣었는지 화면에서 바로 보이게 */
export function sourceLabel(source) {
  if (source === 'manual') return '수기';
  if (source === 'csv') return 'CSV';
  return 'Apify';
}
